import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Pill } from "../../components/common/Primitives";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";
import { Users, ArrowLeft, Plus, Search, X, Save } from "lucide-react";

/**
 * Admin Debt Clients — list + Client 360 detail.
 * Client 360 rolls up every case, call and document for a borrower.
 */
export default function AdminDebtClients() {
  const { id } = useParams();
  return id ? <Client360 clientId={id} /> : <ClientsList />;
}

function ClientsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/debt/clients", { params: q ? { q } : {} });
      setRows(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  return (
    <DashboardShell title="Debt Clients" subtitle="VenturaZ Money · Client 360">
      <Toaster richColors position="top-right" />
      <div className="space-y-4" data-testid="admin-debt-clients">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <Search size={14} className="text-ink-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Name, email, PAN, CIN, GSTIN, UID…"
              className="flex-1 px-3 py-2 border border-navy/15 rounded text-sm" data-testid="clients-search" />
          </div>
          <Button variant="gold" onClick={() => setCreating(true)} testId="clients-new">
            <Plus size={12} className="inline mr-1" />New client
          </Button>
        </div>
        {loading ? (
          <div className="text-sm italic text-ink-muted">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm italic text-ink-muted border border-navy/10 rounded p-6 text-center" data-testid="clients-empty">
            No clients yet.
          </div>
        ) : (
          <div className="border border-navy/10 rounded bg-white/60 overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-navy/[0.04] text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                <tr>
                  <th className="text-left px-3 py-2">UID</th>
                  <th className="text-left px-3 py-2">Legal name</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Contact</th>
                  <th className="text-left px-3 py-2">Industry / State</th>
                  <th className="text-left px-3 py-2">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy/[0.06]">
                {rows.map((c) => (
                  <tr key={c.client_id} className="hover:bg-navy/[0.02]" data-testid={`client-row-${c.client_id}`}>
                    <td className="px-3 py-2 font-mono text-[10px]">
                      <Link to={`/admin/debt/clients/${c.client_id}`} className="text-navy hover:underline">
                        {c.client_uid || c.client_id}
                      </Link>
                    </td>
                    <td className="px-3 py-2 font-heading text-ink">{c.legal_name}</td>
                    <td className="px-3 py-2">{c.borrower_type}</td>
                    <td className="px-3 py-2 font-mono text-[10px]">
                      {c.contact_name}<br />{c.contact_email}
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px]">
                      {c.industry || "—"}<br />{c.hq_state || "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px] text-ink-muted">{formatDateTime(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {creating && <ClientEditor onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </DashboardShell>
  );
}

function ClientEditor({ onClose, onSaved }) {
  const [form, setForm] = useState({
    borrower_type: "business",
    legal_name: "", contact_name: "", contact_email: "", contact_phone: "",
    pan: "", cin: "", gstin: "", industry: "", hq_state: "", city: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.legal_name || !form.contact_name || !form.contact_email) {
      toast.error("Legal name, contact name and email are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/debt/clients", form);
      toast.success("Client created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded max-w-2xl w-full my-6 shadow-lg border border-navy/15" onClick={(e) => e.stopPropagation()} data-testid="client-editor">
        <div className="px-4 py-3 border-b border-navy/[0.08] flex items-center justify-between">
          <div className="overline">New client</div>
          <button onClick={onClose}><X size={16} className="text-ink-muted" /></button>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            ["legal_name", "Legal / trading name *"],
            ["contact_name", "Contact name *"],
            ["contact_email", "Contact email *"],
            ["contact_phone", "Contact phone"],
            ["pan", "PAN"],
            ["cin", "CIN"],
            ["gstin", "GSTIN"],
            ["industry", "Industry"],
            ["hq_state", "HQ state"],
            ["city", "City"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{label}</label>
              <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="w-full mt-0.5 px-3 py-2 border border-navy/15 rounded text-sm" data-testid={`client-${k}`} />
            </div>
          ))}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Borrower type</label>
            <select value={form.borrower_type} onChange={(e) => setForm({ ...form, borrower_type: e.target.value })}
              className="w-full mt-0.5 px-3 py-2 border border-navy/15 rounded text-sm bg-white" data-testid="client-borrower_type">
              <option value="business">Business</option>
              <option value="individual">Individual</option>
            </select>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-navy/[0.08] flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-xs text-ink-muted hover:text-ink px-3 py-2" data-testid="client-cancel">Cancel</button>
          <Button variant="gold" onClick={save} disabled={busy} testId="client-save">
            <Save size={12} className="inline mr-1" />{busy ? "Saving…" : "Create client"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Client360({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/debt/clients/${clientId}`).then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [clientId]);
  if (loading || !data) {
    return <DashboardShell title="Client 360" subtitle="Loading…"><div className="text-sm italic text-ink-muted">Loading…</div></DashboardShell>;
  }
  const c = data.client;
  return (
    <DashboardShell title={c.legal_name} subtitle={`Client · ${c.client_uid || c.client_id}`}>
      <Toaster richColors position="top-right" />
      <div className="space-y-4" data-testid={`admin-client-360-${clientId}`}>
        <Link to="/admin/debt/clients" className="text-xs text-navy hover:underline inline-flex items-center gap-1">
          <ArrowLeft size={12} /> All clients
        </Link>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Cases" value={data.totals.case_count} />
          <Stat label="Requested (₹)" value={Number(data.totals.requested_inr).toLocaleString("en-IN")} />
          <Stat label="Disbursed (₹)" value={Number(data.totals.disbursed_inr).toLocaleString("en-IN")} tone="success" />
          <Stat label="Contact" value={<span className="text-xs">{c.contact_email}</span>} />
        </div>
        <div className="border border-navy/10 rounded bg-white/60 p-4">
          <div className="overline mb-2">Cases</div>
          {data.cases.length === 0 ? (
            <div className="text-xs italic text-ink-muted">No cases linked yet.</div>
          ) : (
            <div className="divide-y divide-navy/[0.06]">
              {data.cases.map((cs) => (
                <Link key={cs.case_id} to={`/admin/debt/${cs.case_id}`} data-testid={`client-case-${cs.case_id}`}
                  className="flex items-center justify-between py-2 hover:bg-navy/[0.02]">
                  <div>
                    <div className="font-heading text-ink text-sm">{cs.case_uid || cs.case_id}</div>
                    <div className="text-[10px] font-mono text-ink-muted">
                      ₹{Number(cs.amount_inr || 0).toLocaleString("en-IN")} · {cs.purpose} · {cs.tenure_months}m
                    </div>
                  </div>
                  <Pill tone="gold">{cs.stage}</Pill>
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="border border-navy/10 rounded bg-white/60 p-4">
          <div className="overline mb-2">Recent calls (across all cases)</div>
          {data.calls.length === 0 ? (
            <div className="text-xs italic text-ink-muted">No calls logged yet.</div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {data.calls.map((k) => (
                <div key={k.call_id} className="border border-navy/[0.06] rounded p-2 text-xs">
                  <div className="text-[10px] font-mono text-ink-muted">
                    {k.direction} · {k.author_email} · {formatDateTime(k.at)}
                  </div>
                  <div className="mt-1"><b>Outcome:</b> {k.outcome}</div>
                  {k.requirement_summary && <div className="text-ink-soft italic">&ldquo;{k.requirement_summary}&rdquo;</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="border border-navy/10 rounded p-3 bg-white/60">
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{label}</div>
      <div className={`font-display text-xl mt-1 ${tone === "success" ? "text-emerald-800" : "text-ink"}`}>{value}</div>
    </div>
  );
}
